import { apiRequest } from './api';
import type {
  Honorary,
  CreateHonoraryDto,
  UpdateHonoraryDto,
  HonorarySummary,
  HonoraryFilters,
  HonoraryStatus,
} from '@/types';

export const honoraryService = {
  /**
   * Lista todos os honorários do usuário com filtros opcionais
   */
  async listHonoraries(filters: HonoraryFilters = {}): Promise<Honorary[]> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.status) params.append('status', filters.status);
    if (filters.legalArea) params.append('legalArea', filters.legalArea);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.installments) params.append('installments', filters.installments.toString());
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.year) params.append('year', filters.year.toString());

    const queryString = params.toString();
    const endpoint = queryString 
      ? `/honorary/honoraries?${queryString}`
      : '/honorary/honoraries';

    return apiRequest<Honorary[]>(endpoint);
  },

  /**
   * Busca um honorário por ID
   */
  async getHonoraryById(id: string): Promise<Honorary> {
    return apiRequest<Honorary>(`/honorary/honoraries/${id}`);
  },

  /**
   * Busca honorários por número do processo
   */
  async getHonoraryByProcessNumber(processNumber: string): Promise<Honorary> {
    return apiRequest<Honorary>(`/honorary/honoraries/process/${processNumber}`);
  },

  /**
   * Busca honorários por customer
   */
  async getHonorariesByCustomer(
    customerId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<Honorary[]> {
    return apiRequest<Honorary[]>(
      `/honorary/honoraries/customer/${customerId}?limit=${limit}&offset=${offset}`
    );
  },

  /**
   * Lista honorários recebidos
   */
  async listReceivedHonoraries(filters: HonoraryFilters = {}): Promise<Honorary[]> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.legalArea) params.append('legalArea', filters.legalArea);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/honorary/honoraries/received?${queryString}`
      : '/honorary/honoraries/received';

    return apiRequest<Honorary[]>(endpoint);
  },

  /**
   * Lista honorários pendentes
   */
  async listPendingHonoraries(filters: HonoraryFilters = {}): Promise<Honorary[]> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.legalArea) params.append('legalArea', filters.legalArea);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.installments) params.append('installments', filters.installments.toString());

    const queryString = params.toString();
    const endpoint = queryString
      ? `/honorary/honoraries/pending?${queryString}`
      : '/honorary/honoraries/pending';

    return apiRequest<Honorary[]>(endpoint);
  },

  /**
   * Cria um novo honorário
   */
  async createHonorary(data: CreateHonoraryDto): Promise<Honorary> {
    return apiRequest<Honorary>(
      '/honorary/honoraries',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Atualiza um honorário existente
   */
  async updateHonorary(id: string, data: UpdateHonoraryDto): Promise<Honorary> {
    return apiRequest<Honorary>(
      `/honorary/honoraries/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Atualiza apenas o status de um honorário
   */
  async updateStatus(id: string, status: HonoraryStatus): Promise<Honorary> {
    return apiRequest<Honorary>(
      `/honorary/honoraries/${id}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    );
  },

  /**
   * Registra um pagamento parcial
   */
  async registerPartialPayment(id: string, paidValue: number): Promise<Honorary> {
    return apiRequest<Honorary>(
      `/honorary/honoraries/${id}/partial-payment`,
      {
        method: 'POST',
        body: JSON.stringify({ paidValue }),
      }
    );
  },

  /**
   * Exporta relatório de honorários
   */
  async exportReport(filters: HonoraryFilters = {}): Promise<Honorary[]> {
    const params = new URLSearchParams();
    
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.status) params.append('status', filters.status);
    if (filters.legalArea) params.append('legalArea', filters.legalArea);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.installments) params.append('installments', filters.installments.toString());

    const queryString = params.toString();
    const endpoint = queryString
      ? `/honorary/honoraries/export/report?${queryString}`
      : '/honorary/honoraries/export/report';

    return apiRequest<Honorary[]>(endpoint);
  },

  /**
   * Obtém resumo de honorários
   */
  async getSummary(filters: {
    month?: number;
    year?: number;
    legalArea?: string;
    customerId?: string;
  } = {}): Promise<HonorarySummary> {
    const params = new URLSearchParams();
    
    if (filters.month) params.append('month', filters.month.toString());
    if (filters.year) params.append('year', filters.year.toString());
    if (filters.legalArea) params.append('legalArea', filters.legalArea);
    if (filters.customerId) params.append('customerId', filters.customerId);

    const queryString = params.toString();
    const endpoint = queryString
      ? `/honorary/honoraries/summary?${queryString}`
      : '/honorary/honoraries/summary';

    return apiRequest<HonorarySummary>(endpoint);
  },

  /**
   * Deleta um honorário
   */
  async deleteHonorary(id: string): Promise<void> {
    return apiRequest<void>(
      `/honorary/honoraries/${id}`,
      {
        method: 'DELETE',
      }
    );
  },
};
